import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import mapValues from 'lodash/mapValues';
import get from 'lodash/get';
import cond from 'lodash/cond';
import range from 'lodash/range';
import styled from 'styled-components';
import isBefore from 'date-fns/isBefore';
import format from 'date-fns-tz/format';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

import { getInstance as getBootstrap } from 'utils/bootstrap';

import { SuiThemeProvider, SuiDropdown, SuiDropdownOption, SuiTextInput } from '@splashthat/sui';
import { createSuiTheme } from '@splashthat/sui/lib/theme';
import { DROPDOWN_INPUT_TYPE } from 'common/entities/form/styleConfiguration/constants';
import transformValueToPixels from 'common/compounds/FormRenderer/utils/transformValueToPixels';

import {
    DEFAULT_INPUT_BORDER_COLOR,
    DEFAULT_INPUT_BORDER_COLOR_HOVER,
    DEFAULT_INPUT_BORDER_RADIUS,
    DEFAULT_INPUT_BORDER_WIDTH,
    DEFAULT_FONT,
    DEFAULT_SMALL_TICKET_FONT_SIZE,
    DEFAULT_INPUT_ANSWER_COLOR,
    DEFAULT_INPUT_PLACEHOLDER_COLOR,
    DEFAULT_INPUT_BACKGROUND_COLOR,
} from 'common/entities/form/styleConfiguration/defaultValues';
import {
    DEFAULT_MIN_QUANTITY_PER_ORDER,
    DEFAULT_MAX_QUANTITY_PER_ORDER,
} from 'common/entities/ticketType/defaultValues';
import { updateFormGroupValue } from '../../../../../actions/form';
import { getTicketTypeQuantityValue } from '../../../../../selectors/form';
import parseFontSize from '../../../../../utils/parseFontSize';
import { TICKET_VALUE_QUANTITY } from '../../../../../constants/ticketTypes';

const EMPTY_DROPDOWN_PLACEHOLDER = 'Select Quantity';
const EMPTY_INPUT_PLACEHOLDER = 'Enter Quantity';
const SOLD_OUT_LABEL = 'SOLD OUT';
const SELECT_QUANTITY_FOR_LABEL = 'Select quantity for';

const getQuantityDropdownLabel = (ticketLabel) => {
    const normalizedTicketLabel = (ticketLabel ?? '').trim();
    const accessibleLabel = normalizedTicketLabel
        ? `${SELECT_QUANTITY_FOR_LABEL} ${normalizedTicketLabel}`
        : EMPTY_DROPDOWN_PLACEHOLDER;
    return <VisuallyHidden.Root>{accessibleLabel}</VisuallyHidden.Root>;
};

const TicketSoldOut = styled.div`
    display: flex;
    align-items: center;
    height: 2.875rem;
    color: ${(props) => props.answerColor};
    font-family: ${(props) => props.fontFamily};
    font-size: ${(props) => parseFontSize(props.fontSize)};
    justify-content: ${(props) => (props.ticketStyle === 'square' ? 'center' : 'right')};
`;

const TicketDateTimeAvailable = styled.div`
    text-align: ${(props) => (props.ticketStyle === 'square' ? 'center' : 'right')};
    color: ${(props) => props.answerColor};
    font-family: ${(props) => props.fontFamily};
    font-size: ${(props) => parseFontSize(props.fontSize)};
`;

const getMaxQuantityValue = (maxQuantity, quantityRemaining) => {
    if (quantityRemaining >= DEFAULT_MAX_QUANTITY_PER_ORDER) {
        return maxQuantity || DEFAULT_MAX_QUANTITY_PER_ORDER;
    }

    return maxQuantity === 0 || maxQuantity > quantityRemaining ? quantityRemaining : maxQuantity;
};

export const getQuantityOptionsArray = (minQuantity, maxQuantity, quantityRemaining) => {
    if (quantityRemaining < DEFAULT_MAX_QUANTITY_PER_ORDER && minQuantity > quantityRemaining) {
        return [];
    }

    const max = getMaxQuantityValue(maxQuantity, quantityRemaining);

    return range(minQuantity, max + 1);
};

const TicketTypeQuantity = ({
    id,
    label,
    quantityStyles,
    minQuantityPerOrder,
    maxQuantityPerOrder,
    disabled,
    quantityRemaining,
    qaId,
    isSoldOut,
    availabilityStart,
    availabilityStartString,
    availabilityEnd,
    availabilityEndString,
    ticketStyle,
}) => {
    const inputType = get(quantityStyles, 'inputType', DROPDOWN_INPUT_TYPE);
    const borderColor = get(quantityStyles, 'colors.default.value', DEFAULT_INPUT_BORDER_COLOR);
    const borderColorHover = get(quantityStyles, 'colors.hover.value', DEFAULT_INPUT_BORDER_COLOR_HOVER);
    const answerColor = get(quantityStyles, 'fontColors.answer.value', DEFAULT_INPUT_ANSWER_COLOR);
    const placeholderColor = get(quantityStyles, 'fontColors.placeholder.value', DEFAULT_INPUT_PLACEHOLDER_COLOR);
    const fontFamily = get(quantityStyles, 'font.value', DEFAULT_FONT);
    const fontSize = get(quantityStyles, 'fontSize', DEFAULT_SMALL_TICKET_FONT_SIZE);
    const inputBackgroundColorDefault = get(
        quantityStyles,
        'inputBackgroundColors.default.value',
        DEFAULT_INPUT_BACKGROUND_COLOR,
    );
    const inputBackgroundColorHover = get(
        quantityStyles,
        'inputBackgroundColors.hover.value',
        DEFAULT_INPUT_BACKGROUND_COLOR,
    );
    const borderWidths = get(quantityStyles, 'sizes', {
        top: DEFAULT_INPUT_BORDER_WIDTH,
        bottom: DEFAULT_INPUT_BORDER_WIDTH,
        right: DEFAULT_INPUT_BORDER_WIDTH,
        left: DEFAULT_INPUT_BORDER_WIDTH,
    });
    const borderRadius = get(quantityStyles, 'radius', DEFAULT_INPUT_BORDER_RADIUS);

    const quantity = useSelector((state) => getTicketTypeQuantityValue(state, { id }));
    const dispatch = useDispatch();
    const onChangeHandler = useCallback(
        (_, value) => dispatch(updateFormGroupValue(id, TICKET_VALUE_QUANTITY, value)),
        [dispatch],
    );

    const isCurrentDateTimeBeforeAvailabilityStart = () =>
        availabilityStart && isBefore(new Date(), new Date(availabilityStart));

    const isCurrentDateTimeAfterAvailabilityEnd = () =>
        availabilityEnd && isBefore(new Date(availabilityEnd), new Date());

    const renderDateTimeAvailability = () => {
        const DATE_TIME_FORMAT = 'MM/dd/yyyy h:mma zzz';
        const AVAILABLE = 'Available:';
        const ENDED = 'Ended:';

        const bootstrap = getBootstrap();
        const timeZoneObj = bootstrap.get('timezones.timezone');
        const timeZone = {
            timeZone: get(timeZoneObj, 'TimeZone.identifier', 'America/New_York'),
        };

        const availabilityStartDateTimeObj = format(Date.parse(availabilityStartString), DATE_TIME_FORMAT, timeZone);
        const availabilityStartDate = availabilityStartDateTimeObj.split(' ')[0];
        const availabilityStartTime = availabilityStartDateTimeObj.split(' ')[1];
        const availabilityStartTimeZone = availabilityStartDateTimeObj.split(' ')[2];
        const availabilityStartDateTime = `${availabilityStartDate} ${availabilityStartTime}`;
        const availabilityEndDateTimeObj = format(Date.parse(availabilityEndString), DATE_TIME_FORMAT, timeZone);
        const availabilityEndDate = availabilityEndDateTimeObj.split(' ')[0];
        const availabilityEndTime = availabilityEndDateTimeObj.split(' ')[1];
        const availabilityEndTimeZone = availabilityEndDateTimeObj.split(' ')[2];
        const availabilityEndDateTime = `${availabilityEndDate} ${availabilityEndTime}`;

        const available = isCurrentDateTimeBeforeAvailabilityStart();

        const availabilityLabel = available ? AVAILABLE : ENDED;

        const availabilityValue = available ? availabilityStartDateTime : availabilityEndDateTime;

        const availabilityTimeZone = available ? availabilityStartTimeZone : availabilityEndTimeZone;

        return (
            <TicketDateTimeAvailable
                data-qa={`${qaId}__label-dateTimeAvailability`}
                answerColor={get(quantityStyles, 'fontColors.answer.value')}
                fontFamily={get(quantityStyles, 'font.value')}
                fontSize={get(quantityStyles, 'fontSize')}
                ticketStyle={ticketStyle}
            >
                <div>{availabilityLabel}</div>
                <div>{availabilityValue}</div>
                <div>{availabilityTimeZone}</div>
            </TicketDateTimeAvailable>
        );
    };

    const renderSoldOut = () => (
        <TicketSoldOut
            data-qa={`${qaId}__label-soldOut`}
            answerColor={get(quantityStyles, 'fontColors.answer.value')}
            fontFamily={get(quantityStyles, 'font.value')}
            fontSize={get(quantityStyles, 'fontSize')}
            ticketStyle={ticketStyle}
        >
            {SOLD_OUT_LABEL}
        </TicketSoldOut>
    );

    const renderDropdown = () => (
        <>
            <SuiDropdown
                placeholder={EMPTY_DROPDOWN_PLACEHOLDER}
                label={getQuantityDropdownLabel(label)}
                fullWidth
                disabled={disabled}
                onChange={onChangeHandler}
                value={quantity}
                data-qa={`${qaId}__input--dropdown`}
            >
                <SuiDropdownOption
                    className="FormNode__TicketTypeQuantityDropdown__option"
                    value={DEFAULT_MIN_QUANTITY_PER_ORDER}
                    displayValue={DEFAULT_MIN_QUANTITY_PER_ORDER}
                />
                {getQuantityOptionsArray(minQuantityPerOrder, maxQuantityPerOrder, quantityRemaining).map((qty) => (
                    <SuiDropdownOption
                        className="FormNode__TicketTypeQuantityDropdown__option"
                        key={qty}
                        value={qty}
                        displayValue={qty}
                    />
                ))}
            </SuiDropdown>
        </>
    );

    const renderTextInput = () => (
        <SuiTextInput
            data-qa={`${qaId}__input--number`}
            fullWidth
            type="number"
            placeholder={EMPTY_INPUT_PLACEHOLDER}
            min={minQuantityPerOrder || DEFAULT_MIN_QUANTITY_PER_ORDER}
            max={maxQuantityPerOrder || DEFAULT_MAX_QUANTITY_PER_ORDER}
            disabled={disabled}
            onChange={onChangeHandler}
            value={quantity}
        />
    );

    const checkIsSoldOut = () =>
        !!isSoldOut ||
        (getQuantityOptionsArray(minQuantityPerOrder, maxQuantityPerOrder, quantityRemaining) || []).length === 0;
    const checkIsDropdownType = () => inputType === DROPDOWN_INPUT_TYPE;
    const checkIsDateTimeAvailable = () =>
        isCurrentDateTimeBeforeAvailabilityStart() || isCurrentDateTimeAfterAvailabilityEnd();

    return (
        <SuiThemeProvider
            theme={createSuiTheme({
                components: {
                    // FIXME having to set the theme on suiField to edit
                    //  SuiTextInput doesn't look very intuitive
                    suiField: {
                        variants: {
                            base: {
                                border: {
                                    radius: transformValueToPixels(borderRadius),
                                    color: borderColor,
                                    hover: {
                                        color: borderColorHover,
                                    },
                                    focus: {
                                        color: borderColorHover,
                                    },
                                    ...mapValues(borderWidths, (size) => transformValueToPixels(size)),
                                },
                                font: {
                                    family: fontFamily,
                                    size: parseFontSize(fontSize),
                                    color: answerColor,
                                    placeholderColor,
                                },
                                background: {
                                    color: inputBackgroundColorDefault,
                                    hover: {
                                        color: inputBackgroundColorHover,
                                    },
                                },
                            },
                        },
                    },
                },
            })}
        >
            {cond([
                [checkIsSoldOut, renderSoldOut],
                [checkIsDateTimeAvailable, renderDateTimeAvailability],
                [checkIsDropdownType, renderDropdown],
                [() => true, renderTextInput],
            ])()}
        </SuiThemeProvider>
    );
};

TicketTypeQuantity.propTypes = {
    id: PropTypes.node,
    disabled: PropTypes.bool.isRequired,
    label: PropTypes.string,
    isSoldOut: PropTypes.bool,
    minQuantityPerOrder: PropTypes.number.isRequired,
    maxQuantityPerOrder: PropTypes.number.isRequired,
    quantityRemaining: PropTypes.number.isRequired,
    qaId: PropTypes.string.isRequired,
    quantityStyles: PropTypes.object,
    availabilityStart: PropTypes.number.isRequired,
    availabilityStartString: PropTypes.string.isRequired,
    availabilityEnd: PropTypes.number.isRequired,
    availabilityEndString: PropTypes.string.isRequired,
    ticketStyle: PropTypes.oneOf(['square', 'stub']).isRequired,
};

TicketTypeQuantity.defaultProps = {
    id: null,
    label: '',
    isDropdown: true,
    isSoldOut: false,
    quantityStyles: {},
};

export default TicketTypeQuantity;
